/**
 * login-page.tsx — Đăng nhập khách doanh nghiệp (/login): hộp nổi trên nền giả lập hệ thống (AuthShell, kiểu SaleSmartly).
 * Sau đăng nhập về trang bị chặn trước đó (D9 `authRedirectTarget`). Email Google chưa có tài khoản → sang Đăng ký,
 * mở thẳng bước "Hoàn tất đăng ký". Trang Platform (/platform/login) tách riêng.
 * Từ "Quên mật khẩu" (khi bật Turnstile) về đây kèm `state.resetEmail`: email điền sẵn + thông báo đã đổi mật khẩu.
 */
import { useCallback, useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { authRedirectTarget } from '@/components/shared/nav-config'
import { clearLogoutNotice, peekLogoutNotice } from '@/lib/session-expiry'
import { AuthLayout } from './auth-layout'
import { useSignupConfig } from './auth-api'
import { LoginForm, type GoogleSignupTicket } from './login-form'
import { useTranslation } from 'react-i18next'

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation('auth')
  const { config } = useSignupConfig()
  // Lý do bị đăng xuất hẳn (tổ chức bị khoá / hết hạn) — đọc một lần.
  const [notice] = useState(peekLogoutNotice)
  const resetEmail = (location.state as { resetEmail?: unknown } | null)?.resetEmail
  const resetDoneFor = typeof resetEmail === 'string' && resetEmail ? resetEmail : ''
  useEffect(() => clearLogoutNotice(), [])

  // Giữ đủ pathname + query + hash của trang bị chặn (vd /billing?payment=…); không có
  // thì về `/` — trang đầu theo quyền (Hộp thư nếu có quyền chat).
  // `?next=` từ trang giới thiệu (Mua gói → màn mua gói) được ưu tiên.
  const from = authRedirectTarget(location.state, location.search)

  const onLoggedIn = useCallback(() => {
    toast.success(t('login.success'))
    navigate(from, { replace: true })
  }, [from, navigate, t])

  const onGoogleSignup = useCallback((ticket: GoogleSignupTicket) => {
    toast.info(t('login.googleNoAccount'))
    navigate(`/register${location.search}`, { state: { googleSignup: ticket } })
  }, [navigate, location.search, t])

  const signupOpen = config?.enabled !== false

  return (
    <AuthLayout
      title={t('login.title')}
      footer={signupOpen ? (
        <>{t('login.noAccount')} <Link to={`/register${location.search}`} className="font-medium text-primary hover:underline">{t('login.registerNow')}</Link></>
      ) : undefined}
    >
      <LoginForm
        initialEmail={resetDoneFor}
        notice={resetDoneFor ? t('login.resetDone') : notice}
        onLoggedIn={onLoggedIn}
        onGoogleSignup={onGoogleSignup}
        forgotLink={<Link to="/forgot-password" className="font-medium text-primary hover:underline">{t('login.resetPassword')}</Link>}
      />
    </AuthLayout>
  )
}
